import { html, View, type Html, rune } from 'rune-ts';
import klass from './VideoSection.module.scss';
import ScrollSmoother from 'gsap/ScrollSmoother';
import { ScrollState, SetScrollIndexEvent } from '../../HandmadeCreatorLandingPage';
import { staticTypoPcMo, typoPcMo } from '../../../../../shared/typography/typo';
import { OnlyYouMessage } from '../../icons';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger.js';
import { ONE_SCROLL_INDEX_MAP_PC } from '../../const';

interface VideoSectionData {
  smoother?: ScrollSmoother;
  scroll_state: ScrollState;
}

export class VideoSection extends View<VideoSectionData> {
  override template(): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const texts = {
      video_card_title_1: html.preventEscape(
        ET(`mps2::landing::handmade::${is_mobile ? 'mo_' : ''}video_card_title_1`),
      ),
      video_card_title_2: html.preventEscape(
        ET(`mps2::landing::handmade::${is_mobile ? 'mo_' : ''}video_card_title_2`),
      ),
      video_card_title_3: html.preventEscape(
        ET(`mps2::landing::handmade::${is_mobile ? 'mo_' : ''}video_card_title_3`),
      ),
    };
    return html`
      <section class="${klass.container}">
        <div class="${klass.video_container} video_1">
          <div class="${klass.video_wrapper} ${klass.blue_bg}">
            <video
              preload="none"
              autoplay=""
              loop=""
              muted=""
              playsinline=""
              class="${klass.motion_video} ${klass.opacity}"
              src="//s3.ap-northeast-2.amazonaws.com/s3.marpple.co/files/u_4648126/2024/10/original/designer-drawing-cut.mp4"
            ></video>
          </div>
          <div class="${klass.slogan}">${OnlyYouMessage}</div>
          <div class="${klass.video_content} video_content">
            <div class="${klass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '28_bold' })}">
              ${texts.video_card_title_1}
            </div>
          </div>
        </div>
        <div class="${klass.video_container} video_2">
          <div class="${klass.video_wrapper}">
            <video
              preload="none"
              autoplay=""
              loop=""
              muted=""
              playsinline=""
              class="${klass.motion_video}"
              src="//s3.ap-northeast-2.amazonaws.com/s3.marpple.co/files/u_4648126/2024/10/original/knitting_neddles.mp4"
            ></video>
          </div>
          <div class="${klass.video_content}">
            <div class="${klass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '28_bold' })}">
              ${texts.video_card_title_2}
            </div>
          </div>
        </div>
        <div class="${klass.video_container} video_3">
          <div class="${klass.video_wrapper}">
            <video
              preload="none"
              autoplay=""
              loop=""
              muted=""
              playsinline=""
              class="${klass.motion_video}"
              src="//s3.ap-northeast-2.amazonaws.com/s3.marpple.co/files/u_4648126/2024/10/original/croissants_tray.mp4"
            ></video>
          </div>
          <div class="${klass.video_content}">
            <div class="${klass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '28_bold' })}">
              ${texts.video_card_title_3}
            </div>
            <div
              class="${klass.description} ${staticTypoPcMo({
                is_mobile,
                pc: 'unica_32_bold',
                mo: 'unica_16_bold',
              })}"
            >
              Make it Real, MarppleShop
            </div>
          </div>
        </div>
      </section>
    `;
  }

  override onRender() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    this.setInitialStatus();
    if (!is_mobile) this.initVideoScrollTrigger();
  }

  setInitialStatus() {
    gsap.set('.video_content', { opacity: 0 });
  }

  initVideoScrollTrigger() {
    ScrollTrigger.create({
      trigger: '.video_2',
      start: 'top top',
      pin: true,
      pinSpacing: false,
      onEnter: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index + 2 },
        });
      },
      onEnterBack: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index + 2 },
        });
      },
    });
    ScrollTrigger.create({
      trigger: '.video_3',
      start: 'top top',
      pin: true,
      pinSpacing: false,
      onEnter: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index + 3 },
        });
      },
      onLeaveBack: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index + 3 },
        });
      },
    });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: `.video_1`,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        pin: true,
      },
      onEnter: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index },
        });
      },
      onEnterBack: () => {
        this.dispatchEvent(SetScrollIndexEvent, {
          bubbles: true,
          detail: { current_index: ONE_SCROLL_INDEX_MAP_PC.video_index + 1 },
        });
      },
    });

    tl.fromTo(`.${klass.slogan}`, { scale: 1 }, { scale: 1.5 })
      .to(`.${klass.opacity}`, { opacity: 1 }, '<')
      .to(`.${klass.slogan}`, { opacity: 0 })
      .to('.video_content', { opacity: 1 }, '<');
  }
}
